import { Avatar, Stack } from '@mantine/core';
import type { AIPersonaOut, Filter } from '@repo/api-codegen';
import { fetchGetPersonas } from '@repo/api-codegen';
import { AIAvatar } from '@repo/common/components/AI/Avatar';
import {
	ALL_REMOVED_USERS,
	FILTER_OPTIONS_DIVIDER,
} from '@repo/common/components/Filter/constants';
import type {
	FilterItem,
	FilterValue,
	FilterValueType,
} from '@repo/common/components/Filter/types';
import {
	FilterOperator,
	FilterOptionType,
	SortValue,
} from '@repo/common/components/Filter/types';
import { IconEmoji } from '@repo/common/components/IconEmoji/IconEmoji';
import { EntityType } from '@repo/common/enums/entityType';
import { getSummaryAndBreadCrumbs } from '@repo/common/utils/breadcrumb';
import { Icon, Text } from '@repo/foundations';
import type { QueryKey } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import type {
	ApiCatalogSort,
	IApiListResponse,
	ISecodaEntity,
	IUser,
	IUserGroup,
	Namespace,
} from '../../api';
import {
	fetchUserGroupList,
	fetchUserList,
	queryClient,
	searchQueryKeyFactory,
} from '../../api';
import { apiClient, getEndpoints } from '../../api/common';
import type { SecodaEntity } from '../../lib/models';
import { search } from '../../lib/models';
import { getEntityDisplayTitle } from '../../lib/utils/entity';
import { SecodaEntityIcon } from '../SecodaEntity';
import { UserAvatar } from '../UserAvatar';

const PERSONA_FALLBACK_LABEL = 'Secoda AI';

type PersonaFilterEntry = {
	item: FilterItem;
	normalizedLabel: string;
};

const personaCache: {
	entries: PersonaFilterEntry[] | null;
	promise: Promise<PersonaFilterEntry[]> | null;
	byId: Map<string, PersonaFilterEntry> | null;
} = {
	entries: null,
	promise: null,
	byId: null,
};

export async function fetchQuery<T>(
	getQueryKey: (page: number, searchTerm: string) => QueryKey,
	getQueryFn: (
		page: number,
		searchTerm: string
	) => Promise<IApiListResponse<T>>,
	page: number,
	searchTerm: string,
	filterFn: (searchTerm: string, result: T[]) => T[]
): Promise<T[]> {
	// check if the entire result set (total_pages == 1) is in the query cache
	const queryState = queryClient.getQueryState<IApiListResponse<T>>(
		getQueryKey(page, '')
	);
	if (queryState?.data?.total_pages === 1) {
		// if it is, we apply a local filter to avoid an extra API call
		return filterFn(searchTerm, queryState.data.results ?? []);
	}

	// call the API
	const { results } = await getQueryFn(page, searchTerm);

	return results;
}

const buildDefaultPersonaFilterEntry = (): PersonaFilterEntry => ({
	item: {
		label: PERSONA_FALLBACK_LABEL,
		value: null,
		icon: <AIAvatar size="md" speed="none" />,
		metadata: { isDefaultPersona: true },
	},
	normalizedLabel: PERSONA_FALLBACK_LABEL.toLowerCase(),
});

export async function activeUsersAndGroupsGetItemsFn(
	page: number = 1,
	searchTerm: string = ''
) {
	const { results: usersResults } = await fetchUserList({
		filters: {
			disabled: false,
			is_service_account: false,
			pending: false,
		},
	});
	const { results: userGroupsResults } = await fetchUserGroupList({});

	const users =
		usersResults
			?.filter(
				(user: IUser) =>
					!searchTerm ||
					user.display_name
						?.toLowerCase()
						?.includes(searchTerm.toLowerCase()) ||
					user.email?.toLowerCase()?.includes(searchTerm.toLowerCase())
			)
			.map((user: IUser) => ({
				label: user.display_name || 'Untitled',
				value: user.id,
				metadata: 'user',
				icon: <UserAvatar key={user.id} user={user} size="xs" />,
			})) ?? [];

	const userGroups =
		userGroupsResults
			?.filter(
				(userGroup: IUserGroup) =>
					!searchTerm ||
					userGroup.name?.toLowerCase()?.includes(searchTerm.toLowerCase())
			)
			.map((userGroup: IUserGroup) => ({
				label: userGroup.name || 'Untitled',
				value: userGroup.id,
				metadata: 'userGroup',
				icon: userGroup.icon ? (
					<IconEmoji value={userGroup.icon} />
				) : (
					<Icon name="users" size="md" />
				),
			})) ?? [];

	return [
		...users,
		...(users.length > 0 && userGroups.length > 0 && !searchTerm
			? [FILTER_OPTIONS_DIVIDER]
			: []),
		...userGroups,
	];
}

export async function allUsersAndGroupsGetItemsFn(
	page: number = 1,
	searchTerm: string = ''
) {
	const { results: usersResults } = await fetchUserList({
		filters: {
			is_service_account: false,
			pending: false,
		},
	});
	const { results: userGroupsResults } = await fetchUserGroupList({});

	const users =
		usersResults
			?.filter(
				(user: IUser) =>
					!user.disabled && // Hide disabled users from individual list
					(!searchTerm ||
						user.display_name
							?.toLowerCase()
							?.includes(searchTerm.toLowerCase()) ||
						user.email?.toLowerCase()?.includes(searchTerm.toLowerCase()))
			)
			.map((user: IUser) => ({
				label: user.display_name || 'Untitled',
				value: user.id,
				metadata: { type: 'user', disabled: user.disabled },
				icon: <UserAvatar key={user.id} user={user} size="xs" />,
			})) ?? [];

	const userGroups =
		userGroupsResults
			?.filter(
				(userGroup: IUserGroup) =>
					!searchTerm ||
					userGroup.name?.toLowerCase()?.includes(searchTerm.toLowerCase())
			)
			.map((userGroup: IUserGroup) => ({
				label: userGroup.name || 'Untitled',
				value: userGroup.id,
				metadata: { type: 'userGroup' },
				icon: userGroup.icon ? (
					<IconEmoji value={userGroup.icon} />
				) : (
					<Icon name="users" size="md" />
				),
			})) ?? [];

	// Add special "Removed Users" option if there are disabled users and no search term
	const disabledUsers =
		usersResults?.filter((user: IUser) => user.disabled) ?? [];
	const removedUsersOption =
		disabledUsers.length > 0 && !searchTerm
			? [
					{
						label: 'Removed Users',
						value: ALL_REMOVED_USERS,
						metadata: { type: 'allDisabledUsers', special: true },
					},
				]
			: [];

	return [
		...removedUsersOption,
		...users,
		...(users.length > 0 && userGroups.length > 0 && !searchTerm
			? [FILTER_OPTIONS_DIVIDER]
			: []),
		...userGroups,
	];
}

export async function convertOwnerFilterToCatalogFilter(
	value: FilterValueType | null
): Promise<Filter | null> {
	const items = (await allUsersAndGroupsGetItemsFn()) ?? [];

	const item = items.find(
		(i) => i !== FILTER_OPTIONS_DIVIDER && i.value === value
	);

	if (!item || item === FILTER_OPTIONS_DIVIDER) {
		return null;
	}

	const metadata = item.metadata as { type: string; special?: boolean };

	// Handle special "ALL Removed Users" case
	if (metadata.type === 'allDisabledUsers' && metadata.special) {
		// Get all disabled user IDs from the current user list
		const { results: usersResults } = await fetchUserList({
			filters: {
				disabled: true,
				is_service_account: false,
				pending: false,
			},
		});

		const disabledUserIds = usersResults?.map((user: IUser) => user.id) ?? [];

		if (disabledUserIds.length === 0) {
			return null;
		}

		return {
			operands: [],
			operator: 'in',
			field: 'owners',
			value: disabledUserIds,
		};
	}

	return {
		operands: [],
		operator: 'exact',
		field: metadata.type === 'user' ? 'owners' : 'owner_groups',
		value: item.value,
	};
}

export async function convertOwnerFilterFromCatalogFilter(
	filter: Filter | null,
	isNegate: boolean = false
): Promise<FilterValue | null> {
	if (!filter) {
		return null;
	}

	// Handle both 'owners' and 'owner_groups' fields
	if (filter.field !== 'owners' && filter.field !== 'owner_groups') {
		return null;
	}

	const filterValue = filter.value ?? null;

	if (filter.operator === 'exact' || filter.operator === 'in') {
		return {
			operator: isNegate ? FilterOperator.IsNot : FilterOperator.Is,
			filterType: FilterOptionType.OWNERS,
			value: filterValue,
		};
	}

	if (filter.operator === 'is_set') {
		return {
			operator: isNegate ? FilterOperator.isNotSet : FilterOperator.isSet,
			filterType: FilterOptionType.OWNERS,
			value: null,
			isSetApplied: !isNegate,
			isNotSetApplied: isNegate,
		};
	}

	return null;
}

export async function convertInitiatedByFilterToCatalogFilter(
	value: FilterValueType | null
): Promise<Filter | null> {
	const items = (await activeUsersAndGroupsGetItemsFn()) ?? [];

	const item = items.find(
		(i) => i !== FILTER_OPTIONS_DIVIDER && i.value === value
	);

	if (!item || item === FILTER_OPTIONS_DIVIDER) {
		return null;
	}

	return {
		operands: [],
		operator: 'exact',
		field: 'performer_id',
		value: item.value,
	};
}

export function buildEntityGetItemsFn(entityType: EntityType) {
	return buildEntityGetItemsFnWithContext(entityType);
}

export function buildEntityGetItemsFnWithContext(
	entityType: EntityType,
	contextFilters?: Filter
) {
	return async (page: number = 1, searchTerm: string = '') => {
		const getFilterParams = (filterPage: number, filterSearchTerm: string) => {
			const filters: Filter = {
				operands: [],
				operator: 'and',
			};
			if (entityType === EntityType.table) {
				filters.operands = filters.operands.concat([
					{
						operands: [],
						operator: 'exact',
						field: 'entity_type',
						value: EntityType.table,
					},
				]);
			} else if (entityType !== EntityType.all) {
				filters.operands = filters.operands.concat([
					{
						operands: [],
						operator: 'exact',
						field: 'entity_type',
						value: entityType,
					},
					{
						operands: [],
						operator: 'exact',
						field: 'native_type',
						value: entityType,
					},
				]);
			}

			// Add context filters (like integration_id) if provided
			if (contextFilters) {
				filters.operands.push(contextFilters);
			}

			return {
				search_term: filterSearchTerm,
				sort: { field: SortValue.RELEVANCE, order: 'desc' } as ApiCatalogSort,
				filters: filters,
				page: filterPage,
			};
		};
		const getQueryKey = (filterPage: number, filterSearchTerm: string) =>
			searchQueryKeyFactory.list(
				page,
				getFilterParams(filterPage, filterSearchTerm)
			);
		const getQueryFn = async (filterPage: number, filterSearchTerm: string) => {
			const { data } = await search(
				getFilterParams(filterPage, filterSearchTerm)
			);
			return data;
		};

		const results = await fetchQuery<ISecodaEntity>(
			getQueryKey,
			getQueryFn,
			page,
			searchTerm,
			(filterSearchTerm, result) =>
				result.filter(
					(entity) =>
						!searchTerm ||
						entity.title?.toLowerCase().includes(filterSearchTerm.toLowerCase())
				)
		);

		return (
			results?.map((entity) => {
				let entityId = entity.id;

				if (
					entity.entity_type === EntityType.user &&
					entity.display_metadata.owners?.[0]?.id
				) {
					entityId = entity.display_metadata.owners?.[0]?.id;
				}

				return {
					label: getEntityDisplayTitle(entity),
					value: entityId,
					metadata: entity,
					icon: (
						<SecodaEntityIcon entity={{ ...entity, id: entityId }} size={20} />
					),
				};
			}) ?? []
		);
	};
}

export function buildGetItemsById<T = ISecodaEntity>(
	namespace: Namespace,
	getIcon?: (entity: T) => ReactNode
) {
	return async (ids: string[]): Promise<FilterItem[]> => {
		const promises = ids.map(async (id) => {
			try {
				const url = getEndpoints(namespace).byId(id);
				const entity = await apiClient.get(url).then((data) => data.data);

				return {
					label: entity.title ?? entity.name,
					value: entity.id,
					metadata: entity,
					icon: getIcon ? (
						getIcon(entity)
					) : (
						<SecodaEntityIcon entity={entity} size={20} />
					),
				};
			} catch {
				// ignore errors - most likely due to a deleted entity
			}

			return null;
		});

		return (await Promise.all(promises)).filter(Boolean) as FilterItem[];
	};
}

export async function buildGetItemsByIdFromSearch(ids: string[]) {
	const searchParams = {
		sort: { field: '_score', order: 'desc' } as ApiCatalogSort,
		filters: {
			operands: [],
			operator: 'in',
			field: 'id',
			value: ids,
		} as Filter,
		page: 1,
	};

	const { results } = await queryClient.fetchQuery<
		IApiListResponse<ISecodaEntity>
	>({
		queryKey: searchQueryKeyFactory.list(1, searchParams),
		queryFn: async () => {
			const { data } = await search(searchParams);
			return data;
		},
	});

	return (
		results?.map((entity) => {
			let entityId = entity.id;

			if (
				entity.entity_type === EntityType.user &&
				entity.display_metadata.owners?.[0]?.id
			) {
				entityId = entity.display_metadata.owners?.[0]?.id;
			}

			return {
				label: entity.title || 'Untitled',
				value: entityId,
				metadata: entity,
				icon: (
					<SecodaEntityIcon entity={{ ...entity, id: entityId }} size={20} />
				),
			};
		}) ?? []
	);
}

export function convertBooleanFilterToIsSetFilter(field: string) {
	return async function (value: FilterValueType | null): Promise<Filter> {
		const filterValue: Filter = {
			operands: [],
			operator: 'is_set',
			field,
			value: null,
		};

		return value
			? filterValue
			: {
					operator: 'not',
					operands: [filterValue],
				};
	};
}

export function convertMonitorFilterToCatalogFilter() {
	return async function (
		value: FilterValueType | null
	): Promise<Filter | null> {
		if (!value) {
			return null;
		}

		// If value is 'any', just check if monitors exist
		if (value === 'any') {
			return {
				operands: [],
				operator: 'is_set',
				field: 'monitors',
				value: null,
			};
		}

		// If value is a specific monitor type, filter by that type
		if (typeof value === 'string' && value !== 'any') {
			return {
				operator: 'and',
				operands: [
					{
						operands: [],
						operator: 'is_set',
						field: 'monitors',
						value: null,
					},
					{
						operands: [],
						operator: 'in',
						field: 'monitor_metric_type',
						value: [value],
					},
				],
			};
		}

		return null;
	};
}

export function convertLineageFilterToCatalogFilter() {
	return async function (value: FilterValueType | null): Promise<Filter> {
		if (value === 'UPSTREAM') {
			return {
				operator: 'and',
				operands: [
					{
						operator: 'is_set',
						operands: [],
						field: 'vulnerability_score',
						value: null,
					},
					{
						operator: 'gt',
						operands: [],
						field: 'vulnerability_score',
						value: 0,
					},
				],
				field: null,
				value: null,
			};
		}

		return {
			operator: 'and',
			operands: [
				{
					operator: 'is_set',
					operands: [],
					field: 'impact_score',
					value: null,
				},
				{
					operator: 'gt',
					operands: [],
					field: 'impact_score',
					value: 0,
				},
			],
			field: null,
			value: null,
		};
	};
}

export function renderFilterItemWithBreadcrumbs(withType?: boolean) {
	return function render(item: FilterItem) {
		const entity = item.metadata as SecodaEntity;

		const breadcrumbs = getSummaryAndBreadCrumbs(
			withType ? entity.native_type || entity.entity_type : null,
			entity.search_metadata
		);

		return (
			<Stack spacing={0}>
				<Text size="sm" lineClamp={1}>
					{entity.title || 'Untitled'}
				</Text>
				<Text size="xs" color="text/secondary/default" lineClamp={1}>
					{breadcrumbs}
				</Text>
			</Stack>
		);
	};
}

export async function usersGetItemsFn(
	page: number = 1,
	searchTerm: string = ''
) {
	const { results: usersResults } = await fetchUserList({
		filters: {
			disabled: false,
			pending: false,
		},
	});

	const users =
		usersResults
			?.filter(
				(user: IUser) =>
					!searchTerm ||
					user.display_name
						?.toLowerCase()
						?.includes(searchTerm.toLowerCase()) ||
					user.email?.toLowerCase()?.includes(searchTerm.toLowerCase())
			)
			.map((user: IUser) => ({
				label: user.display_name || 'Untitled',
				value: user.id,
				metadata: 'user',
				icon: <UserAvatar key={user.id} user={user} size="xs" />,
			})) ?? [];

	return users;
}

export async function usersGetItemsByIdFn(ids: string[]) {
	const { results: usersResults } = await fetchUserList({
		filters: {
			disabled: false,
			pending: false,
		},
	});

	const users =
		usersResults
			.filter((user: IUser) => ids.includes(user.id))
			.map((user: IUser) => ({
				label: user.display_name || 'Untitled',
				value: user.id,
				metadata: 'user',
				icon: <UserAvatar key={user.id} user={user} size="xs" />,
			})) ?? [];

	return users;
}

export async function createdByGetItemsFn(
	page: number = 1,
	searchTerm: string = ''
) {
	const { results: usersResults } = await fetchUserList({
		filters: {
			disabled: false,
			is_service_account: false,
			pending: false,
		},
	});

	const users =
		usersResults
			?.filter(
				(user: IUser) =>
					!searchTerm ||
					user.display_name
						?.toLowerCase()
						?.includes(searchTerm.toLowerCase()) ||
					user.email?.toLowerCase()?.includes(searchTerm.toLowerCase())
			)
			.map((user: IUser) => ({
				label: user.display_name || 'Untitled',
				value: user.id,
				metadata: { type: 'user', disabled: user.disabled },
				icon: <UserAvatar key={user.id} user={user} size="xs" />,
			})) ?? [];

	return [
		...(users.length > 0 && !searchTerm ? [FILTER_OPTIONS_DIVIDER] : []),
		...users,
	];
}

const buildPersonaFilterEntry = (persona: AIPersonaOut): PersonaFilterEntry => {
	const label = persona.name?.trim() || PERSONA_FALLBACK_LABEL;
	const isDefaultPersona = persona.is_default;

	return {
		item: {
			label,
			value: isDefaultPersona ? null : persona.id,
			icon:
				persona.avatar_url && !isDefaultPersona ? (
					<Avatar src={persona.avatar_url} alt={label} size="md" radius="xl" />
				) : (
					<AIAvatar size="md" speed="none" />
				),
			metadata: {
				isDefaultPersona,
				personaId: persona.id,
			},
		},
		normalizedLabel: label.toLowerCase(),
	};
};

const ensurePersonaIndexes = (entries: PersonaFilterEntry[]) => {
	if (!personaCache.byId) {
		personaCache.byId = new Map();
		entries.forEach((entry) => {
			// Store by value (may be null for default persona)
			if (entry.item.value !== null) {
				personaCache.byId!.set(String(entry.item.value), entry);
			}
			// Also store by personaId from metadata (for non-default personas)
			const personaId = entry.item.metadata?.personaId;
			if (personaId) {
				personaCache.byId!.set(personaId, entry);
			}
		});
	}
};

const loadPersonaFilterEntries = async (): Promise<PersonaFilterEntry[]> => {
	if (personaCache.entries) {
		ensurePersonaIndexes(personaCache.entries);
		return personaCache.entries;
	}

	if (!personaCache.promise) {
		personaCache.promise = (async () => {
			try {
				const personas = await fetchGetPersonas({});
				const entries = personas.map(buildPersonaFilterEntry);
				const hasDefaultPersona = entries.some(
					(entry) => entry.item.metadata?.isDefaultPersona
				);
				const personaEntries = hasDefaultPersona
					? entries
					: [buildDefaultPersonaFilterEntry(), ...entries];
				personaCache.entries = personaEntries;
				ensurePersonaIndexes(personaEntries);
				return personaEntries;
			} catch (error) {
				// eslint-disable-next-line no-console
				console.error('Failed to fetch personas for filters', error);
				throw error;
			}
		})();
	}

	try {
		return await personaCache.promise;
	} catch {
		personaCache.promise = null;
		personaCache.entries = null;
		personaCache.byId = null;
		return [];
	}
};

export const aiPersonasGetItemsFn = async (
	_page: number = 1,
	searchTerm: string = ''
): Promise<FilterItem[]> => {
	const normalizedSearch = searchTerm.trim().toLowerCase();
	const entries = await loadPersonaFilterEntries();

	if (!normalizedSearch) {
		return entries.map((entry) => entry.item);
	}

	return entries
		.filter((entry) => entry.normalizedLabel.includes(normalizedSearch))
		.map((entry) => entry.item);
};

export const aiPersonasGetItemsById = async (
	ids: (string | null)[]
): Promise<FilterItem[]> => {
	if (!ids.length) {
		return [];
	}

	const entries = await loadPersonaFilterEntries();
	ensurePersonaIndexes(entries);

	if (!personaCache.byId) {
		return [];
	}

	return ids.reduce<FilterItem[]>((acc, id) => {
		// Handle null ID (default persona)
		if (id === null || id === 'null') {
			const defaultEntry = entries.find(
				(entry) => entry.item.metadata?.isDefaultPersona
			);
			if (defaultEntry) {
				acc.push(defaultEntry.item);
			}
			return acc;
		}

		const entry = personaCache.byId?.get(id);
		if (entry) {
			acc.push(entry.item);
		}
		return acc;
	}, []);
};
